import {
	AvalancheConstants,
	ObjectSize,
	Position,
	ReelConstants,
	SymbolPosition,
	SymbolSizes,
} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {Layout} from '@fantasmagames/slot/src/layout/Layout';
import {ViewTypes} from '@fantasmagames/slot/src/common/view/View';
import {Bezier, IEasingFunction} from '@fantasmagames/core/src/tween/Easing';

export class GameReelConstants extends ReelConstants {

	protected static TOP_SYMBOL_SLOT: SymbolSizes[] = [];
	protected static BOTTOM_SYMBOL_SLOT: SymbolSizes[] = [];

	protected static TOP_PART_ROWS_AMOUNT: number = 3;
	protected static BOTTOM_PART_ROWS_AMOUNT: number = 3;

	protected static TOP_REELS_OFFSETS: Position[] = [];
	protected static BOTTOM_REELS_OFFSETS: Position[] = [];

	public static initializeFromLayoutGameSpecific(layout: Layout, viewType: ViewTypes = ReelConstants.DEFAULT_VIEW): void {
		const reelPosition: Position = layout.getAbsolutePosition('TOP_REEL', viewType);
		const topSymbolSize: ObjectSize = layout.getSize('SYMBOL_01', viewType);
		const bottomSymbolSize: ObjectSize = layout.getSize('SYMBOL_16', viewType);
		const bottomReelPosition: Position = layout.getAbsolutePosition('BOTTOM_REEL', viewType);

		GameReelConstants.TOP_REELS_OFFSETS[viewType] = {x: reelPosition.x, y: reelPosition.y};
		GameReelConstants.TOP_SYMBOL_SLOT[viewType] = {width: topSymbolSize.width, height: topSymbolSize.height};
		GameReelConstants.BOTTOM_SYMBOL_SLOT[viewType] = {width: bottomSymbolSize.width, height: bottomSymbolSize.height};
		GameReelConstants.BOTTOM_REELS_OFFSETS[viewType] = {x: bottomReelPosition.x, y: bottomReelPosition.y};

		GameReelConstants.recalculateNumbersGameSpecific();
	}

	public static recalculateNumbersGameSpecific(viewType: ViewTypes = ReelConstants.DEFAULT_VIEW): void {
		ReelConstants.REELS_CENTER[viewType] = {
			x: GameReelConstants.TOP_REELS_OFFSETS[viewType].x + (GameReelConstants.TOP_SYMBOL_SLOT[viewType].width * ReelConstants.REELS_AMOUNT) / 2,
			y: GameReelConstants.TOP_REELS_OFFSETS[viewType].y + (GameReelConstants.TOP_SYMBOL_SLOT[viewType].height * ReelConstants.ROW_AMOUNT) / 2,
		};
	}

	public static getTopReelsOffsets(viewType: ViewTypes = ReelConstants.DEFAULT_VIEW): SymbolPosition {
		return GameReelConstants.TOP_REELS_OFFSETS[viewType];
	}

	public static getBottomReelsOffsets(viewType: ViewTypes = ReelConstants.DEFAULT_VIEW): SymbolPosition {
		return GameReelConstants.BOTTOM_REELS_OFFSETS[viewType];
	}

	public static getTopSymbolSlot(viewType: ViewTypes = ReelConstants.DEFAULT_VIEW): SymbolSizes {
		return GameReelConstants.TOP_SYMBOL_SLOT[viewType];
	}

	public static getBottomSymbolSlot(viewType: ViewTypes = ReelConstants.DEFAULT_VIEW): SymbolSizes {
		return GameReelConstants.BOTTOM_SYMBOL_SLOT[viewType];
	}

	public static getSpecificRowSymbolSlot(rowIndex: number): SymbolSizes {
		if (rowIndex >= 0 && rowIndex < 3) {
			return GameReelConstants.getTopSymbolSlot();
		} else if (rowIndex >= 3 && rowIndex < ReelConstants.ROW_AMOUNT) {
			return GameReelConstants.getBottomSymbolSlot();
		}
		throw new Error(`GameReelConstants::getSpecificRowSymbolSlot impossible row ${rowIndex}`);
	}

	public static getSpecificRowYSymbolPosition(rowIndex: number): number {
		if (rowIndex >= 0 && rowIndex < 3) {
			return GameReelConstants.getTopSymbolSlot().height * rowIndex;
		} else if (rowIndex >= 3 && rowIndex < ReelConstants.ROW_AMOUNT) {
			return GameReelConstants.getBottomSymbolSlot().height * (rowIndex - 3);
		}

		throw new Error(`GameReelConstants::getSpecificRowYSymbolPosition impossible row ${rowIndex}`);
	}

	public static getReelsOffsetForRow(rowIndex: number): Position {
		if (rowIndex >= 0 && rowIndex < 3) {
			return GameReelConstants.getTopReelsOffsets();
		} else if (rowIndex >= 3 && rowIndex < ReelConstants.ROW_AMOUNT) {
			return GameReelConstants.getBottomReelsOffsets();
		}

		throw new Error(`GameReelConstants::getReelsOffsetForRow impossible row ${rowIndex}`);
	}

	public static getGameSymbolPosition(pos: SymbolPosition): Position {
		if (pos.y >= 0 && pos.y < 3) {
			return {x: GameReelConstants.getTopReelsOffsets().x + GameReelConstants.getTopSymbolSlot().width * pos.x
				, y: GameReelConstants.getTopReelsOffsets().y + GameReelConstants.getTopSymbolSlot().height * pos.y};
		} else if (pos.y >= 3 && pos.y < ReelConstants.ROW_AMOUNT) {
			return {x: GameReelConstants.getBottomReelsOffsets().x + GameReelConstants.getBottomSymbolSlot().width * pos.x
				, y: GameReelConstants.getBottomReelsOffsets().y + GameReelConstants.getBottomSymbolSlot().height * (pos.y - 3)};
		}

		throw new Error(`GameReelConstants::getSpecificRowYSymbolPosition impossible row ${pos.y}`);
	}

	public static getGameSymbolCenterPosition(pos: SymbolPosition): Position {
		if (pos.y >= 0 && pos.y < 3) {
			return {x: GameReelConstants.getTopReelsOffsets().x + GameReelConstants.getTopSymbolSlot().width * pos.x + GameReelConstants.getTopSymbolSlot().width / 2
				, y: GameReelConstants.getTopReelsOffsets().y + GameReelConstants.getTopSymbolSlot().height * pos.y + GameReelConstants.getTopSymbolSlot().height / 2};
		} else if (pos.y >= 3 && pos.y < ReelConstants.ROW_AMOUNT) {
			return {x: GameReelConstants.getBottomReelsOffsets().x + GameReelConstants.getBottomSymbolSlot().width * pos.x + GameReelConstants.getBottomSymbolSlot().width / 2
				, y: GameReelConstants.getBottomReelsOffsets().y + GameReelConstants.getBottomSymbolSlot().height * (pos.y - 3) + GameReelConstants.getBottomSymbolSlot().height / 2};
		}

		throw new Error(`GameReelConstants::getSpecificRowYSymbolPosition impossible row ${pos.y}`);
	}

	public static getGameSymbolSize(pos: SymbolPosition): SymbolSizes {
		if (pos.y >= 0 && pos.y < 3) {
			return GameReelConstants.getTopSymbolSlot();
		} else if (pos.y >= 3 && pos.y < ReelConstants.ROW_AMOUNT) {
			return GameReelConstants.getBottomSymbolSlot();
		}

		throw new Error(`GameReelConstants::getGameSymbolSize impossible row ${pos.y}`);
	}

	public static getAllReelsSize(): ObjectSize {
		return {width: GameReelConstants.getTopSymbolSlot().width * GameReelConstants.REELS_AMOUNT
			, height: GameReelConstants.getTopSymbolSlot().height * GameReelConstants.TOP_PART_ROWS_AMOUNT
				+ GameReelConstants.getBottomSymbolSlot().height * GameReelConstants.BOTTOM_PART_ROWS_AMOUNT};
	}
}

export class AvalancheDropConfig {
	dropDuration: number;
	rowDelay: number;
	reelDelay: number;
	rowDelayTop: number;
	reelDelayTop: number;
	topDelay: number;
}

export class GameAvalancheConstants extends AvalancheConstants {

	public static DROP_EASING: IEasingFunction = Bezier.bezier(0.2, 1.2, 0.7, 1);

	public static dropOutConfig: AvalancheDropConfig;
	public static dropInConfig: AvalancheDropConfig;
	public static dropExistingConfig: AvalancheDropConfig;

	public static initDropConfigs(isRegulatedMarket: boolean): void {

		this.dropOutConfig = new AvalancheDropConfig();
		this.dropInConfig = new AvalancheDropConfig();
		this.dropExistingConfig = new AvalancheDropConfig();

		if (isRegulatedMarket) {
			this.dropOutConfig.dropDuration = 260;
			this.dropOutConfig.rowDelay = 80;
			this.dropOutConfig.reelDelay = 80;

			this.dropInConfig.dropDuration = 340;
			this.dropInConfig.rowDelay = 105;
			this.dropInConfig.reelDelay = 330;
			this.dropInConfig.rowDelayTop = 20;
			this.dropInConfig.reelDelayTop = 60;
			this.dropInConfig.topDelay = 1425;
		} else {
			this.dropOutConfig.dropDuration = 200;
			this.dropOutConfig.rowDelay = 50;
			this.dropOutConfig.reelDelay = 40;

			this.dropInConfig.dropDuration = 320;
			this.dropInConfig.rowDelay = 65;
			this.dropInConfig.reelDelay = 150;
			this.dropInConfig.rowDelayTop = 13;
			this.dropInConfig.reelDelayTop = 30;
			this.dropInConfig.topDelay = 710;
		}

		this.dropExistingConfig.dropDuration = 400;
		this.dropExistingConfig.rowDelay = 20;
		this.dropExistingConfig.reelDelay = 20;
	}
}


// WEBPACK FOOTER //
// ./src/reels/GameReelConstants.ts